
import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { SpotifyTrack, TrackDownload } from '@/lib/types';
import { getTrackDownload, incrementDownloadCount } from '@/lib/trackDownload';
import { getTrackDetails } from '@/lib/spotify';
import { useSpotifyDeepLink } from '@/hooks/useSpotifyDeepLink';

export function useDownload(trackId: string) {
  const [isLiked, setIsLiked] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [countdownActive, setCountdownActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10);
  const [downloadReady, setDownloadReady] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [downloadInfo, setDownloadInfo] = useState<TrackDownload | null>(null);
  const [track, setTrack] = useState<SpotifyTrack | null>(null);
  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement | null>(null);
  const { toast } = useToast();
  const openSpotifyLink = useSpotifyDeepLink();

  useEffect(() => {
    const loadTrackData = async () => {
      setIsLoading(true);
      try {
        // Don't try to load anything if trackId is missing or invalid
        if (!trackId || trackId.trim() === '') {
          setError("No track ID provided. Please check the URL.");
          setIsLoading(false);
          return;
        }
        
        console.log("Fetching download data for track ID:", trackId);
        const downloadData = await getTrackDownload(trackId);
        
        if (!downloadData) {
          console.error('Download not found for ID:', trackId);
          setError("We couldn't find this download. The track may have been removed or the URL is incorrect.");
          toast({
            title: "Track not found",
            description: "We couldn't find this download. It may have been removed.",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
        
        setDownloadInfo(downloadData);
        console.log("Download data fetched successfully:", downloadData);
        
        try {
          // Try to get Spotify details, but don't fail if we can't
          const spotifyDetails = await getTrackDetails(downloadData.trackId);
          setTrack(spotifyDetails);
          
          if (spotifyDetails.previewUrl) {
            const audio = new Audio(spotifyDetails.previewUrl);
            setAudioPlayer(audio);
            
            audio.addEventListener('ended', () => setIsPlaying(false));
            audio.addEventListener('pause', () => setIsPlaying(false));
            audio.addEventListener('play', () => setIsPlaying(true));
          }
        } catch (spotifyError) {
          console.error("Error getting Spotify details:", spotifyError);
          // Create minimal track details from the download info
          setTrack({
            id: downloadData.trackId,
            name: downloadData.trackName || downloadData.fileName,
            artist: downloadData.artistName || "Unknown Artist",
            albumCover: "",
            spotifyUrl: downloadData.spotifyUrl
          });
        }
      } catch (error) {
        console.error("Error loading track:", error);
        setError("There was a problem loading this download. Please try again.");
        toast({
          title: "Error loading track",
          description: "There was a problem loading this download. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadTrackData();
    
    return () => {
      if (audioPlayer) {
        audioPlayer.pause();
        audioPlayer.src = '';
      }
    };
  }, [trackId, toast]);

  const handlePlayPreview = () => {
    if (!audioPlayer) return;
    
    if (isPlaying) {
      audioPlayer.pause();
    } else {
      audioPlayer.play().catch(err => {
        console.error("Error playing audio:", err);
        toast({
          title: "Playback error",
          description: "Could not play audio preview. Please try again.",
          variant: "destructive",
        });
      });
    }
  };

  const handleLikeTrack = () => {
    if (!track) return;
    
    // Use our new deep link handler instead of directly opening a new tab
    if (track.spotifyUrl) {
      openSpotifyLink(track.spotifyUrl);
    }
    
    setIsLiked(true);
    setCountdownActive(true);
    
    let countdown = 10;
    const timer = setInterval(() => {
      countdown -= 1;
      setTimeLeft(countdown);
      
      if (countdown <= 0) {
        clearInterval(timer);
        setCountdownActive(false);
        setDownloadReady(true);
        
        toast({
          title: "Download unlocked!",
          description: "Thanks for supporting the artist. Your download is now ready."
        });
      }
    }, 1000);
  };

  const handleDownload = async () => {
    if (!downloadReady || !downloadInfo) return;
    
    setIsDownloading(true);
    
    try {
      await incrementDownloadCount(downloadInfo.id);
      
      const link = document.createElement('a');
      link.href = downloadInfo.fileUrl;
      link.setAttribute('download', downloadInfo.fileName);
      link.setAttribute('target', '_blank');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
      toast({
        title: "Download started",
        description: `${downloadInfo.fileName} is being downloaded.`
      });
    } catch (error) {
      console.error("Download error:", error);
      toast({
        title: "Download failed",
        description: "There was a problem downloading the file. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    isLiked,
    isPlaying,
    countdownActive,
    timeLeft,
    downloadReady,
    isDownloading,
    isLoading,
    error,
    downloadInfo,
    track,
    handlePlayPreview,
    handleLikeTrack,
    handleDownload
  };
}
